/**
 * Modules Store - управление микрофронтендами
 *
 * Отвечает за:
 * - Загрузку списка модулей от API
 * - Кеширование манифестов
 * - Загрузку и монтирование MF
 * - Управление состоянием модулей
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getApiClient } from '@/services/ApiClient'
import { getRegistryService } from '@/services/registry'
import { getEventBus } from '@/shell/EventBus'
import { createModuleLoader, type ModuleState } from '@/shell/ModuleLoader'
import type { ModuleManifest, OrgContext, UserContext } from '@/shell/types'

export const useModulesStore = defineStore('modules', () => {
  // Сервисы
  const registry = getRegistryService()
  const bus = getEventBus()
  const apiClient = getApiClient()
  const loader = createModuleLoader(bus, apiClient)

  // Состояние
  const manifests = ref<ModuleManifest[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // Computed: модули по секциям меню
  const mainModules = computed(() =>
    manifests.value
      .filter((m) => m.menu.section === 'main' && !m.menu.hidden)
      .sort((a, b) => a.menu.order - b.menu.order)
  )

  const toolModules = computed(() =>
    manifests.value
      .filter((m) => m.menu.section === 'tools' && !m.menu.hidden)
      .sort((a, b) => a.menu.order - b.menu.order)
  )

  const adminModules = computed(() =>
    manifests.value
      .filter((m) => m.menu.section === 'admin' && !m.menu.hidden)
      .sort((a, b) => a.menu.order - b.menu.order)
  )

  // Computed: модули доступные без авторизации
  const publicModules = computed(() => manifests.value.filter((m) => !m.requiresAuth))

  // Computed: все загруженные модули
  const loadedModules = computed(() => loader.getLoadedModules())

  // Computed: ID загруженных модулей
  const loadedIds = computed(() => new Set(loadedModules.value.map((m) => m.manifest.id)))

  /**
   * Загрузить список модулей для организации
   */
  async function fetchModules(orgSlug: string): Promise<void> {
    if (loading.value) return

    loading.value = true
    error.value = null

    try {
      const response = await registry.getOrgModules(orgSlug)
      manifests.value = response.modules
      initialized.value = true

      bus.emit('modules:loaded', { count: response.modules.length })
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load modules'
      bus.emit('modules:error', { error: e })
    } finally {
      loading.value = false
    }
  }

  /**
   * Получить манифест модуля по ID
   */
  function getManifest(moduleId: string): ModuleManifest | undefined {
    return manifests.value.find((m) => m.id === moduleId)
  }

  /**
   * Проверить, загружен ли модуль
   */
  function isLoaded(moduleId: string): boolean {
    return loader.isLoaded(moduleId)
  }

  /**
   * Проверить, смонтирован ли модуль
   */
  function isMounted(moduleId: string): boolean {
    return loader.isMounted(moduleId)
  }

  /**
   * Получить состояние модуля
   */
  function getModuleState(moduleId: string): ModuleState | undefined {
    return loader.getModuleState(moduleId)
  }

  /**
   * Загрузить модуль (без монтирования)
   */
  async function loadModule(moduleId: string): Promise<void> {
    const manifest = getManifest(moduleId)
    if (!manifest) {
      throw new Error(`Module ${moduleId} not found in manifests`)
    }

    await loader.loadModule(manifest)
  }

  /**
   * Монтировать модуль в контейнер
   */
  async function mountModule(
    moduleId: string,
    container: HTMLElement,
    options: {
      user?: UserContext
      org: OrgContext
      locale?: string
      theme?: 'light' | 'dark'
      initialRoute?: string
    }
  ): Promise<void> {
    const manifest = getManifest(moduleId)
    if (!manifest) {
      throw new Error(`Module ${moduleId} not found in manifests`)
    }

    await loader.mountModule(manifest, {
      container,
      user: options.user,
      org: options.org,
      locale: options.locale ?? 'ru',
      theme: options.theme ?? 'dark',
      mode: 'spa',
      initialRoute: options.initialRoute,
    })
  }

  /**
   * Размонтировать модуль
   */
  function unmountModule(moduleId: string): void {
    loader.unmountModule(moduleId)
  }

  /**
   * Размонтировать все модули
   */
  function unmountAll(): void {
    loader.unmountAll()
  }

  /**
   * Preload модулей (загрузка в фоне)
   */
  async function preloadModules(moduleIds?: string[]): Promise<void> {
    const toPreload = moduleIds
      ? manifests.value.filter((m) => moduleIds.includes(m.id))
      : publicModules.value

    await loader.preloadModules(toPreload)
  }

  /**
   * Проверить доступ к модулю
   */
  function checkAccess(
    moduleId: string,
    userRoles: string[] = [],
    isAuthenticated = false
  ): { allowed: boolean; reason?: string } {
    const manifest = getManifest(moduleId)
    if (!manifest) {
      return { allowed: false, reason: 'not_found' }
    }

    return registry.checkModuleAccess(manifest, userRoles, isAuthenticated)
  }

  /**
   * Обновить список модулей (force refresh)
   */
  async function refreshModules(orgSlug: string): Promise<void> {
    registry.invalidateOrgCache(orgSlug)
    await fetchModules(orgSlug)
  }

  /**
   * Сбросить store
   */
  function reset(): void {
    unmountAll()
    manifests.value = []
    loading.value = false
    error.value = null
    initialized.value = false
  }

  return {
    // Состояние
    manifests,
    loading,
    error,
    initialized,

    // Computed
    mainModules,
    toolModules,
    adminModules,
    publicModules,
    loadedModules,
    loadedIds,

    // Действия
    fetchModules,
    getManifest,
    isLoaded,
    isMounted,
    getModuleState,
    loadModule,
    mountModule,
    unmountModule,
    unmountAll,
    preloadModules,
    checkAccess,
    refreshModules,
    reset,
  }
})
